import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  TimePicker,
  message,
} from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import {
  AddFitnessCustomSlots,
  AdminBookFitnessSession,
  BlockFitnessSlots,
  CancelFitnessSession,
  CheckFitnessLearnerStatus,
  CreateFitnessTrainer,
  GetFitnessAvailableSlots,
  GetFitnessWorkingHours,
  ListFitnessSessions,
  ListFitnessTrainers,
  RescheduleFitnessSession,
  SetFitnessWorkingHours,
  UpdateFitnessTrainer,
} from "../../redux/actions/FitnessAction";

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const STATUS_COLORS = {
  CONFIRMED: "green",
  CANCELLED: "red",
  COMPLETED: "blue",
  PENDING: "orange",
};

const FitnessManagement = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("sessions");

  const [trainers, setTrainers] = useState([]);
  const [trainersLoading, setTrainersLoading] = useState(false);
  const [createTrainerOpen, setCreateTrainerOpen] = useState(false);
  const [createTrainerLoading, setCreateTrainerLoading] = useState(false);
  const [createTrainerForm] = Form.useForm();
  const [editTrainerOpen, setEditTrainerOpen] = useState(false);
  const [editTrainerLoading, setEditTrainerLoading] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [editTrainerForm] = Form.useForm();

  const [workingHoursForm] = Form.useForm();
  const [workingHoursLoading, setWorkingHoursLoading] = useState(false);
  const [workingHoursFetching, setWorkingHoursFetching] = useState(false);
  const [isHolidayChecked, setIsHolidayChecked] = useState(false);

  const [customSlotsForm] = Form.useForm();
  const [customSlotsLoading, setCustomSlotsLoading] = useState(false);

  const [blockSlotsForm] = Form.useForm();
  const [blockSlotsLoading, setBlockSlotsLoading] = useState(false);
  const [blockEntireDay, setBlockEntireDay] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionFilters, setSessionFilters] = useState({});
  const [sessionFilterForm] = Form.useForm();

  const [bookForm] = Form.useForm();
  const [bookLoading, setBookLoading] = useState(false);
  const [learnerLookupLoading, setLearnerLookupLoading] = useState(false);
  const [learnerInfo, setLearnerInfo] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelForm] = Form.useForm();

  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleForm] = Form.useForm();
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false);

  const fetchTrainers = useCallback(() => {
    setTrainersLoading(true);
    dispatch(
      ListFitnessTrainers(
        {},
        (list) => {
          setTrainers(list);
          setTrainersLoading(false);
        },
        (err) => {
          message.error(err);
          setTrainersLoading(false);
        }
      )
    );
  }, [dispatch]);

  const loadWorkingHours = useCallback(
    (trainerId, dayOfWeek) => {
      if (!trainerId || !dayOfWeek) return;
      setWorkingHoursFetching(true);
      dispatch(
        GetFitnessWorkingHours(
          { trainerId, dayOfWeek },
          (workingHours) => {
            if (workingHours) {
              const shifts = (workingHours.shifts || []).map((s) => ({
                startTime: s.startTime
                  ? moment(s.startTime, "HH:mm")
                  : null,
                endTime: s.endTime ? moment(s.endTime, "HH:mm") : null,
              }));
              workingHoursForm.setFieldsValue({
                isHoliday: !!workingHours.isHoliday,
                shifts: shifts.length ? shifts : [{}],
              });
              setIsHolidayChecked(!!workingHours.isHoliday);
            } else {
              workingHoursForm.setFieldsValue({
                isHoliday: false,
                shifts: [{}],
              });
              setIsHolidayChecked(false);
            }
            setWorkingHoursFetching(false);
          },
          (err) => {
            message.error(err);
            setWorkingHoursFetching(false);
          }
        )
      );
    },
    [dispatch, workingHoursForm]
  );

  const fetchSessions = useCallback(
    (page = 1, filters = sessionFilters) => {
      setSessionsLoading(true);
      const params = {
        page,
        limit: 20,
        ...filters,
      };
      dispatch(
        ListFitnessSessions(
          params,
          ({ sessions: list, pagination }) => {
            setSessions(list);
            setSessionTotal(pagination?.totalRecords ?? list.length);
            setSessionPage(pagination?.currentPage ?? page);
            setSessionsLoading(false);
          },
          (err) => {
            message.error(err);
            setSessionsLoading(false);
          }
        )
      );
    },
    [dispatch, sessionFilters]
  );

  useEffect(() => {
    fetchTrainers();
  }, [fetchTrainers]);

  useEffect(() => {
    if (activeTab === "sessions") {
      fetchSessions(1);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateTrainer = (values) => {
    setCreateTrainerLoading(true);
    const payload = {
      name: values.name,
      email: values.email,
      phoneNumber: values.phoneNumber,
      meetingUrl: values.meetingUrl || null,
      specializations: values.specializations || [],
      bio: values.bio || "",
      maxDailySessions: values.maxDailySessions ?? 8,
    };
    dispatch(
      CreateFitnessTrainer(
        payload,
        () => {
          message.success("Trainer created successfully");
          setCreateTrainerLoading(false);
          setCreateTrainerOpen(false);
          createTrainerForm.resetFields();
          fetchTrainers();
        },
        (err) => {
          message.error(err);
          setCreateTrainerLoading(false);
        }
      )
    );
  };

  const openEditTrainer = (trainer) => {
    setEditingTrainer(trainer);
    editTrainerForm.setFieldsValue({
      name: trainer.name,
      email: trainer.email,
      phoneNumber: trainer.phoneNumber,
      meetingUrl: trainer.meetingUrl || "",
      specializations: trainer.specializations || [],
      bio: trainer.bio || "",
      maxDailySessions: trainer.maxDailySessions ?? 8,
      isActive: trainer.isActive !== false,
    });
    setEditTrainerOpen(true);
  };

  const handleUpdateTrainer = (values) => {
    if (!editingTrainer?._id) return;
    setEditTrainerLoading(true);
    const payload = {
      name: values.name,
      email: values.email,
      phoneNumber: values.phoneNumber,
      meetingUrl: values.meetingUrl || null,
      specializations: values.specializations || [],
      bio: values.bio || "",
      maxDailySessions: values.maxDailySessions ?? 8,
      isActive: values.isActive !== false,
    };
    dispatch(
      UpdateFitnessTrainer(
        editingTrainer._id,
        payload,
        () => {
          message.success("Trainer updated successfully");
          setEditTrainerLoading(false);
          setEditTrainerOpen(false);
          setEditingTrainer(null);
          fetchTrainers();
        },
        (err) => {
          message.error(err);
          setEditTrainerLoading(false);
        }
      )
    );
  };

  const handleSetWorkingHours = (values) => {
    setWorkingHoursLoading(true);
    const isHoliday = !!values.isHoliday;
    const shifts = isHoliday
      ? []
      : (values.shifts || [])
          .filter((s) => s?.startTime && s?.endTime)
          .map((s) => ({
            startTime: moment(s.startTime).format("HH:mm"),
            endTime: moment(s.endTime).format("HH:mm"),
          }));
    dispatch(
      SetFitnessWorkingHours(
        {
          trainerId: values.trainerId,
          dayOfWeek: values.dayOfWeek,
          shifts,
          isHoliday,
        },
        (res) => {
          message.success(res?.message || "Working hours updated");
          setWorkingHoursLoading(false);
          loadWorkingHours(values.trainerId, values.dayOfWeek);
        },
        (err) => {
          message.error(err);
          setWorkingHoursLoading(false);
        }
      )
    );
  };

  const handleAddCustomSlots = (values) => {
    setCustomSlotsLoading(true);
    const slots = (values.slots || []).map((s) => ({
      startTime: moment(s.startTime).format("HH:mm"),
      endTime: moment(s.endTime).format("HH:mm"),
    }));
    dispatch(
      AddFitnessCustomSlots(
        {
          trainerId: values.trainerId,
          specificDate: values.date.format("YYYY-MM-DD"),
          slots,
          reason: values.reason || "",
        },
        () => {
          message.success("Custom slots added");
          setCustomSlotsLoading(false);
          customSlotsForm.resetFields(["slots", "reason", "date"]);
        },
        (err) => {
          message.error(err);
          setCustomSlotsLoading(false);
        }
      )
    );
  };

  const handleBlockSlots = (values) => {
    setBlockSlotsLoading(true);
    dispatch(
      BlockFitnessSlots(
        {
          trainerId: values.trainerId,
          date: values.date.format("YYYY-MM-DD"),
          startTime: blockEntireDay
            ? undefined
            : moment(values.startTime).format("HH:mm"),
          endTime: blockEntireDay
            ? undefined
            : moment(values.endTime).format("HH:mm"),
          blockEntireDay,
          reason: values.reason || "",
        },
        () => {
          message.success("Slots blocked successfully");
          setBlockSlotsLoading(false);
          blockSlotsForm.resetFields();
          setBlockEntireDay(false);
        },
        (err) => {
          message.error(err);
          setBlockSlotsLoading(false);
        }
      )
    );
  };

  const lookupLearner = () => {
    const mobileNumber = bookForm.getFieldValue("mobileNumber");
    const countryCode = bookForm.getFieldValue("countryCode") || "+91";
    if (!mobileNumber) {
      message.warning("Enter learner mobile number");
      return;
    }
    setLearnerLookupLoading(true);
    setLearnerInfo(null);
    dispatch(
      CheckFitnessLearnerStatus(
        { mobileNumber, countryCode },
        (data) => {
          const learner = data?.learner ?? {};
          const learnerId =
            data?.learnerId ?? learner?._id ?? learner?.id ?? null;
          const normalized = {
            exists: data?.exists ?? !!learnerId,
            eligibleForTrial:
              data?.eligibleForTrial ??
              (data?.isFitnessTrialBooked === false) ??
              (learner?.isFitnessTrialBooked === false),
            learnerId,
            name: data?.name ?? learner?.name ?? null,
            phoneNumber:
              data?.phoneNumber ?? learner?.phoneNumber ?? mobileNumber,
            countryCode:
              data?.countryCode ?? learner?.countryCode ?? countryCode,
            isFitnessTrialBooked:
              data?.isFitnessTrialBooked ?? learner?.isFitnessTrialBooked,
          };
          setLearnerInfo(normalized);
          if (learnerId) {
            bookForm.setFieldsValue({ learnerId });
          }
          setLearnerLookupLoading(false);
          if (!normalized.exists) {
            message.warning("Learner not found");
          }
        },
        (err) => {
          message.error(err);
          setLearnerLookupLoading(false);
        }
      )
    );
  };

  const loadAvailableSlots = () => {
    const sessionDate = bookForm.getFieldValue("sessionDate");
    const trainerId = bookForm.getFieldValue("trainerId");
    if (!sessionDate) {
      message.warning("Select a session date first");
      return;
    }
    setSlotsLoading(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    dispatch(
      GetFitnessAvailableSlots(
        {
          sessionDate: sessionDate.format("YYYY-MM-DD"),
          ...(trainerId ? { trainerId } : {}),
          timezone: "Asia/Kolkata",
        },
        (data) => {
          setAvailableSlots(data?.slots ?? []);
          setSlotsLoading(false);
          if (!(data?.slots?.length > 0)) {
            message.info("No available slots for this date");
          }
        },
        (err) => {
          message.error(err);
          setSlotsLoading(false);
        }
      )
    );
  };

  const handleBookSession = (values) => {
    if (!selectedSlot) {
      message.warning("Select an available slot");
      return;
    }
    if (!values.learnerId) {
      message.warning("Look up learner first to get learner ID");
      return;
    }
    setBookLoading(true);
    const trainerId =
      values.trainerId ||
      selectedSlot?.availableTrainers?.[0]?.id ||
      selectedSlot?.availableTrainers?.[0]?._id;
    dispatch(
      AdminBookFitnessSession(
        {
          learnerId: values.learnerId,
          trainerId,
          sessionDate: values.sessionDate.format("YYYY-MM-DD"),
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
          sessionType: values.sessionType || "TRIAL",
          notes: values.notes || "",
          timezone: "Asia/Kolkata",
        },
        (res) => {
          message.success(res?.message || "Session booked successfully");
          setBookLoading(false);
          bookForm.resetFields();
          setLearnerInfo(null);
          setAvailableSlots([]);
          setSelectedSlot(null);
          if (activeTab === "sessions") fetchSessions(1);
        },
        (err) => {
          message.error(err);
          setBookLoading(false);
        }
      )
    );
  };

  const openCancelModal = (session) => {
    setCancelTarget(session);
    cancelForm.resetFields();
    setCancelModalOpen(true);
  };

  const handleCancelSession = (values) => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    dispatch(
      CancelFitnessSession(
        {
          sessionId: cancelTarget._id,
          cancellationReason: values.cancellationReason,
          cancelledBy: "ADMIN",
        },
        () => {
          message.success("Session cancelled");
          setCancelLoading(false);
          setCancelModalOpen(false);
          fetchSessions(sessionPage);
        },
        (err) => {
          message.error(err);
          setCancelLoading(false);
        }
      )
    );
  };

  const openRescheduleModal = (session) => {
    setRescheduleTarget(session);
    rescheduleForm.resetFields();
    setRescheduleSlots([]);
    setRescheduleModalOpen(true);
  };

  const loadRescheduleSlots = () => {
    const newSessionDate = rescheduleForm.getFieldValue("newSessionDate");
    if (!newSessionDate || !rescheduleTarget) return;
    setRescheduleSlotsLoading(true);
    const trainer =
      rescheduleTarget?.trainer ?? rescheduleTarget?.trainerId ?? {};
    const trainerId = trainer?._id || trainer?.id;
    dispatch(
      GetFitnessAvailableSlots(
        {
          sessionDate: newSessionDate.format("YYYY-MM-DD"),
          ...(trainerId ? { trainerId } : {}),
          timezone: "Asia/Kolkata",
        },
        (data) => {
          setRescheduleSlots(data?.slots ?? []);
          setRescheduleSlotsLoading(false);
        },
        (err) => {
          message.error(err);
          setRescheduleSlotsLoading(false);
        }
      )
    );
  };

  const handleReschedule = (values) => {
    if (!rescheduleTarget) return;
    const slotKey = values.slotKey;
    const slot = rescheduleSlots.find(
      (s) => `${s.startTime}-${s.endTime}` === slotKey
    );
    if (!slot) {
      message.warning("Select a new slot");
      return;
    }
    setRescheduleLoading(true);
    dispatch(
      RescheduleFitnessSession(
        {
          sessionId: rescheduleTarget._id,
          newSessionDate: values.newSessionDate.format("YYYY-MM-DD"),
          newStartTime: slot.startTime,
          newEndTime: slot.endTime,
          timezone: "Asia/Kolkata",
          reason: values.reason || "",
        },
        () => {
          message.success("Session rescheduled");
          setRescheduleLoading(false);
          setRescheduleModalOpen(false);
          fetchSessions(sessionPage);
        },
        (err) => {
          message.error(err);
          setRescheduleLoading(false);
        }
      )
    );
  };

  const applySessionFilters = (values) => {
    const filters = {};
    if (values.status) filters.status = values.status;
    if (values.trainerId) filters.trainerId = values.trainerId;
    if (values.dateRange?.length === 2) {
      filters.startDate = values.dateRange[0].format("YYYY-MM-DD");
      filters.endDate = values.dateRange[1].format("YYYY-MM-DD");
    }
    setSessionFilters(filters);
    fetchSessions(1, filters);
  };

  const trainerColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
    {
      title: "Specializations",
      dataIndex: "specializations",
      key: "specializations",
      render: (specs) =>
        (specs || []).map((s) => (
          <Tag key={s} color="blue">
            {s}
          </Tag>
        )),
    },
    {
      title: "Max Daily",
      dataIndex: "maxDailySessions",
      key: "maxDailySessions",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (v) => (
        <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Meet URL",
      dataIndex: "meetingUrl",
      key: "meetingUrl",
      ellipsis: true,
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noreferrer">
            Link
          </a>
        ) : (
          "Auto-generate"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button size="small" onClick={() => openEditTrainer(record)}>
          Edit
        </Button>
      ),
    },
  ];

  const getLearner = (r) => r.learner ?? r.learnerId ?? {};
  const getTrainer = (r) => r.trainer ?? r.trainerId ?? {};
  const getMeetingUrl = (r) =>
    r.meetingUrl ?? r.meetingDetails?.meetingUrl ?? null;

  const sessionColumns = [
    {
      title: "Date",
      dataIndex: "sessionDate",
      key: "sessionDate",
    },
    {
      title: "Time",
      key: "time",
      render: (_, r) => `${r.startTime} - ${r.endTime}`,
    },
    {
      title: "Learner",
      key: "learner",
      render: (_, r) => {
        const learner = getLearner(r);
        return (
          <div>
            <div>{learner?.name || "-"}</div>
            <div style={{ color: "#888", fontSize: 12 }}>
              {learner?.phoneNumber || ""}
            </div>
          </div>
        );
      },
    },
    {
      title: "Trainer",
      key: "trainer",
      render: (_, r) => getTrainer(r)?.name || "-",
    },
    {
      title: "Type",
      dataIndex: "sessionType",
      key: "sessionType",
      render: (t) => <Tag>{t || "-"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Meet",
      key: "meetingUrl",
      render: (_, r) => {
        const url = getMeetingUrl(r);
        return url ? (
          <a href={url} target="_blank" rel="noreferrer">
            Join
          </a>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        record.status === "CONFIRMED" ? (
          <Space>
            <Button size="small" onClick={() => openRescheduleModal(record)}>
              Reschedule
            </Button>
            <Button
              size="small"
              danger
              onClick={() => openCancelModal(record)}
            >
              Cancel
            </Button>
          </Space>
        ) : (
          "-"
        ),
    },
  ];

  const trainerOptions = trainers.map((t) => (
    <Option key={t._id} value={t._id}>
      {t.name}
    </Option>
  ));

  return (
    <div>
      <PageBreadcrumb route={["Fitness", "Management"]} />

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Sessions" key="sessions">
            <Form
              form={sessionFilterForm}
              layout="inline"
              onFinish={applySessionFilters}
              style={{ marginBottom: 16 }}
            >
              <Form.Item name="status" label="Status">
                <Select allowClear placeholder="All" style={{ width: 140 }}>
                  <Option value="CONFIRMED">Confirmed</Option>
                  <Option value="CANCELLED">Cancelled</Option>
                  <Option value="COMPLETED">Completed</Option>
                </Select>
              </Form.Item>
              <Form.Item name="trainerId" label="Trainer">
                <Select
                  allowClear
                  placeholder="All trainers"
                  style={{ width: 180 }}
                  showSearch
                  optionFilterProp="children"
                >
                  {trainerOptions}
                </Select>
              </Form.Item>
              <Form.Item name="dateRange" label="Date range">
                <DatePicker.RangePicker />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Filter
                  </Button>
                  <Button
                    onClick={() => {
                      sessionFilterForm.resetFields();
                      setSessionFilters({});
                      fetchSessions(1, {});
                    }}
                  >
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Form>

            <Table
              rowKey={(r) => r._id}
              columns={sessionColumns}
              dataSource={sessions}
              loading={sessionsLoading}
              pagination={{
                current: sessionPage,
                total: sessionTotal,
                pageSize: 20,
                onChange: (p) => fetchSessions(p),
              }}
              scroll={{ x: 900 }}
            />
          </TabPane>

          <TabPane tab="Book Session" key="book">
            <Row gutter={24}>
              <Col xs={24} lg={14}>
                <Form
                  form={bookForm}
                  layout="vertical"
                  onFinish={handleBookSession}
                  initialValues={{ countryCode: "+91", sessionType: "TRIAL" }}
                >
                  <Row gutter={12}>
                    <Col span={6}>
                      <Form.Item name="countryCode" label="Code">
                        <Input placeholder="+91" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="mobileNumber"
                        label="Learner Mobile"
                        rules={[
                          { required: true, message: "Mobile required" },
                          {
                            pattern: /^\d{10}$/,
                            message: "Enter 10-digit number",
                          },
                        ]}
                      >
                        <Input placeholder="9876543210" maxLength={10} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label=" ">
                        <Button
                          onClick={lookupLearner}
                          loading={learnerLookupLoading}
                          block
                        >
                          Lookup
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>

                  {learnerInfo && (
                    <Card
                      size="small"
                      style={{ marginBottom: 16, background: "#fafafa" }}
                    >
                      <Space direction="vertical" size={0}>
                        <div>
                          <strong>{learnerInfo.name || "Unknown"}</strong>
                          {learnerInfo.exists ? (
                            <Tag color="green" style={{ marginLeft: 8 }}>
                              Exists
                            </Tag>
                          ) : (
                            <Tag color="red" style={{ marginLeft: 8 }}>
                              Not found
                            </Tag>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          ID: {learnerInfo.learnerId || "-"} | Trial eligible:{" "}
                          {learnerInfo.eligibleForTrial ? "Yes" : "No"}
                        </div>
                      </Space>
                    </Card>
                  )}

                  <Form.Item name="learnerId" label="Learner ID" hidden>
                    <Input />
                  </Form.Item>

                  <Form.Item name="trainerId" label="Trainer (optional)">
                    <Select
                      allowClear
                      placeholder="Auto-assign if empty"
                      showSearch
                      optionFilterProp="children"
                    >
                      {trainerOptions}
                    </Select>
                  </Form.Item>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        name="sessionDate"
                        label="Session Date"
                        rules={[{ required: true, message: "Date required" }]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label=" ">
                        <Button onClick={loadAvailableSlots} loading={slotsLoading}>
                          Load Available Slots
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>

                  {slotsLoading ? (
                    <Spin />
                  ) : availableSlots.length > 0 ? (
                    <Form.Item label="Available Slots">
                      <Space wrap>
                        {availableSlots.map((slot) => {
                          const key = `${slot.startTime}-${slot.endTime}`;
                          const selected =
                            selectedSlot &&
                            selectedSlot.startTime === slot.startTime &&
                            selectedSlot.endTime === slot.endTime;
                          return (
                            <Button
                              key={key}
                              type={selected ? "primary" : "default"}
                              onClick={() => setSelectedSlot(slot)}
                            >
                              {slot.displayTime ||
                                `${slot.startTime} - ${slot.endTime}`}
                            </Button>
                          );
                        })}
                      </Space>
                    </Form.Item>
                  ) : null}

                  <Form.Item name="sessionType" label="Session Type">
                    <Select>
                      <Option value="TRIAL">Trial</Option>
                      <Option value="REGULAR">Regular</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="notes" label="Notes">
                    <TextArea rows={2} placeholder="Booked by admin / BDA" />
                  </Form.Item>

                  <Button type="primary" htmlType="submit" loading={bookLoading}>
                    Book Session
                  </Button>
                </Form>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Trainers" key="trainers">
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" onClick={() => setCreateTrainerOpen(true)}>
                Create Trainer
              </Button>
            </div>
            <Table
              rowKey={(r) => r._id}
              columns={trainerColumns}
              dataSource={trainers}
              loading={trainersLoading}
              pagination={false}
              scroll={{ x: 800 }}
            />
          </TabPane>

          <TabPane tab="Working Hours" key="working-hours">
            <Spin spinning={workingHoursFetching}>
              <Form
                form={workingHoursForm}
                layout="vertical"
                onFinish={handleSetWorkingHours}
                style={{ maxWidth: 560 }}
              >
                <Form.Item
                  name="trainerId"
                  label="Trainer"
                  rules={[{ required: true, message: "Select trainer" }]}
                >
                  <Select
                    placeholder="Select trainer"
                    showSearch
                    optionFilterProp="children"
                    onChange={(trainerId) => {
                      const dayOfWeek = workingHoursForm.getFieldValue("dayOfWeek");
                      loadWorkingHours(trainerId, dayOfWeek);
                    }}
                  >
                    {trainerOptions}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="dayOfWeek"
                  label="Day of Week"
                  rules={[{ required: true, message: "Select day" }]}
                >
                  <Select
                    placeholder="Select day"
                    onChange={(dayOfWeek) => {
                      const trainerId = workingHoursForm.getFieldValue("trainerId");
                      loadWorkingHours(trainerId, dayOfWeek);
                    }}
                  >
                    {DAYS.map((d) => (
                      <Option key={d} value={d}>
                        {d}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="isHoliday" valuePropName="checked">
                  <Checkbox
                    onChange={(e) => setIsHolidayChecked(e.target.checked)}
                  >
                    Mark as holiday (no shifts)
                  </Checkbox>
                </Form.Item>
                {!isHolidayChecked && (
                  <Form.List name="shifts" initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Space
                            key={key}
                            align="baseline"
                            style={{ display: "flex", marginBottom: 8 }}
                          >
                            <Form.Item
                              {...rest}
                              name={[name, "startTime"]}
                              rules={[{ required: true, message: "Start" }]}
                            >
                              <TimePicker format="HH:mm" placeholder="Start" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, "endTime"]}
                              rules={[{ required: true, message: "End" }]}
                            >
                              <TimePicker format="HH:mm" placeholder="End" />
                            </Form.Item>
                            {fields.length > 1 && (
                              <Button danger onClick={() => remove(name)}>
                                Remove
                              </Button>
                            )}
                          </Space>
                        ))}
                        <Form.Item>
                          <Button type="dashed" onClick={() => add()} block>
                            Add Shift
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={workingHoursLoading}
                >
                  Save Working Hours
                </Button>
              </Form>
            </Spin>
          </TabPane>

          <TabPane tab="Custom Slots" key="custom-slots">
            <Form
              form={customSlotsForm}
              layout="vertical"
              onFinish={handleAddCustomSlots}
              style={{ maxWidth: 560 }}
            >
              <Form.Item
                name="trainerId"
                label="Trainer"
                rules={[{ required: true, message: "Select trainer" }]}
              >
                <Select
                  placeholder="Select trainer"
                  showSearch
                  optionFilterProp="children"
                >
                  {trainerOptions}
                </Select>
              </Form.Item>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: "Select date" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.List name="slots" initialValue={[{}]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...rest }) => (
                      <Space
                        key={key}
                        align="baseline"
                        style={{ display: "flex", marginBottom: 8 }}
                      >
                        <Form.Item
                          {...rest}
                          name={[name, "startTime"]}
                          rules={[{ required: true, message: "Start" }]}
                        >
                          <TimePicker format="HH:mm" placeholder="Start" />
                        </Form.Item>
                        <Form.Item
                          {...rest}
                          name={[name, "endTime"]}
                          rules={[{ required: true, message: "End" }]}
                        >
                          <TimePicker format="HH:mm" placeholder="End" />
                        </Form.Item>
                        {fields.length > 1 && (
                          <Button danger onClick={() => remove(name)}>
                            Remove
                          </Button>
                        )}
                      </Space>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block>
                        Add Slot
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
              <Form.Item name="reason" label="Reason">
                <Input placeholder="Special morning workshop slots" />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={customSlotsLoading}
              >
                Add Custom Slots
              </Button>
            </Form>
          </TabPane>

          <TabPane tab="Block / Leave" key="block-slots">
            <Form
              form={blockSlotsForm}
              layout="vertical"
              onFinish={handleBlockSlots}
              style={{ maxWidth: 560 }}
            >
              <Form.Item
                name="trainerId"
                label="Trainer"
                rules={[{ required: true, message: "Select trainer" }]}
              >
                <Select
                  placeholder="Select trainer"
                  showSearch
                  optionFilterProp="children"
                >
                  {trainerOptions}
                </Select>
              </Form.Item>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: "Select date" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={blockEntireDay}
                  onChange={(e) => setBlockEntireDay(e.target.checked)}
                >
                  Block entire day
                </Checkbox>
              </Form.Item>
              {!blockEntireDay && (
                <Space>
                  <Form.Item
                    name="startTime"
                    label="Start"
                    rules={[{ required: true, message: "Start required" }]}
                  >
                    <TimePicker format="HH:mm" />
                  </Form.Item>
                  <Form.Item
                    name="endTime"
                    label="End"
                    rules={[{ required: true, message: "End required" }]}
                  >
                    <TimePicker format="HH:mm" />
                  </Form.Item>
                </Space>
              )}
              <Form.Item name="reason" label="Reason">
                <Input placeholder="Sick leave / dentist appointment" />
              </Form.Item>
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={blockSlotsLoading}
              >
                Block Slots
              </Button>
            </Form>
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Create Fitness Trainer"
        visible={createTrainerOpen}
        onCancel={() => setCreateTrainerOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={createTrainerForm}
          layout="vertical"
          onFinish={handleCreateTrainer}
          initialValues={{ maxDailySessions: 8 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone"
            rules={[{ required: true, message: "Phone required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="meetingUrl" label="Meeting URL (optional)">
            <Input placeholder="Leave empty for auto Google Meet" />
          </Form.Item>
          <Form.Item name="specializations" label="Specializations">
            <Select mode="tags" placeholder="Yoga, Strength, etc." />
          </Form.Item>
          <Form.Item name="bio" label="Bio">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="maxDailySessions" label="Max Daily Sessions">
            <InputNumber min={1} max={20} style={{ width: "100%" }} />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createTrainerLoading}
            block
          >
            Create
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Edit Trainer"
        visible={editTrainerOpen}
        onCancel={() => {
          setEditTrainerOpen(false);
          setEditingTrainer(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editTrainerForm}
          layout="vertical"
          onFinish={handleUpdateTrainer}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone"
            rules={[{ required: true, message: "Phone required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="meetingUrl" label="Meeting URL (optional)">
            <Input placeholder="Personal Google Meet link" />
          </Form.Item>
          <Form.Item name="specializations" label="Specializations">
            <Select mode="tags" placeholder="Yoga, Strength, etc." />
          </Form.Item>
          <Form.Item name="bio" label="Bio">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="maxDailySessions" label="Max Daily Sessions">
            <InputNumber min={1} max={20} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked">
            <Checkbox>Active (accepts bookings)</Checkbox>
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={editTrainerLoading}
            block
          >
            Save Changes
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Cancel Session"
        visible={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={cancelForm} layout="vertical" onFinish={handleCancelSession}>
          <Form.Item
            name="cancellationReason"
            label="Reason"
            rules={[{ required: true, message: "Reason required" }]}
          >
            <TextArea rows={3} />
          </Form.Item>
          <Button type="primary" danger htmlType="submit" loading={cancelLoading}>
            Confirm Cancel
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Reschedule Session"
        visible={rescheduleModalOpen}
        onCancel={() => setRescheduleModalOpen(false)}
        footer={null}
        destroyOnClose
        width={520}
      >
        <Form
          form={rescheduleForm}
          layout="vertical"
          onFinish={handleReschedule}
        >
          <Form.Item
            name="newSessionDate"
            label="New Date"
            rules={[{ required: true, message: "Date required" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              onChange={() => {
                setRescheduleSlots([]);
                rescheduleForm.setFieldsValue({ slotKey: undefined });
              }}
            />
          </Form.Item>
          <Form.Item>
            <Button onClick={loadRescheduleSlots} loading={rescheduleSlotsLoading}>
              Load Slots
            </Button>
          </Form.Item>
          <Form.Item
            name="slotKey"
            label="New Slot"
            rules={[{ required: true, message: "Select slot" }]}
          >
            <Select placeholder="Select slot" loading={rescheduleSlotsLoading}>
              {rescheduleSlots.map((s) => (
                <Option
                  key={`${s.startTime}-${s.endTime}`}
                  value={`${s.startTime}-${s.endTime}`}
                >
                  {s.displayTime || `${s.startTime} - ${s.endTime}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={rescheduleLoading}>
            Reschedule
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default FitnessManagement;
